import type { EliteOnboardingStatusResponse } from '../../../types/misc.types'
import { EliteLandingPageState } from '../../../types/misc.types'
import { EliteMembershipCurrentPlanCard } from '../../../components/elite/dashboard/EliteMembershipCurrentPlanCard'
import { EliteMembershipRenewalSection } from '../../../components/elite/dashboard/EliteMembershipRenewalSection'
import { EliteMembershipRenewalStatusCard } from '../../../components/elite/dashboard/EliteMembershipRenewalStatusCard'
import { EliteMembershipUpgradeSection } from '../../../components/elite/dashboard/EliteMembershipUpgradeSection'
import {
  getEliteSubscriptionValidityDateLabel,
  isEliteAutoPayActive,
  shouldShowEliteRenewalPrompt
} from '../../../components/elite/dashboard/eliteMembershipRenewalWindow'
import {
  getEliteCurrentPlanTenure,
  getEliteFocusEligiblePlan,
  getElitePlanStatusLabel,
  getElitePlanTenureLabel,
  getEliteSubscriptionPlanPriceDisplayExGst
} from '../../../components/elite/dashboard/eliteOnboardingFocusPlan'
import { convertEpochToDate, roundOffAndFormatNumberWithCurrency } from '../../../utils/utils'
import NewEliteFamilyPrimaryNonEliteOfferCard, {
  EliteFamilyOfferCardIntent
} from '../../new-elite-landing/components/family/NewEliteFamilyPrimaryNonEliteOfferCard'
import type { FamilyOnboardingViewProps } from '../../new-elite-landing/pages/family/familyOnboardingViewProps'

type ElitePlansEliteMembershipStatusProps = {
  eliteOnboardingStatus?: EliteOnboardingStatusResponse
  familyConfig?: FamilyOnboardingViewProps
  isStatusLoading?: boolean
}

export function ElitePlansEliteMembershipStatus({
  eliteOnboardingStatus,
  familyConfig,
  isStatusLoading = false
}: ElitePlansEliteMembershipStatusProps) {
  const showRenewalPrompt = shouldShowEliteRenewalPrompt(eliteOnboardingStatus)
  const hasUpgradePlan = Boolean(
    eliteOnboardingStatus?.subscriptionDetails?.upgradeDetails?.upgradePlanDetails
  )
  const isAutoPayActive = isEliteAutoPayActive(eliteOnboardingStatus)
  const isExpiredWithoutAutoPay =
    eliteOnboardingStatus?.eliteLandingPageState === EliteLandingPageState.PLAN_EXPIRED &&
    !isAutoPayActive

  if (isStatusLoading) {
    return (
      <div className='w-full px-5 pt-4' data-testid='elite-plans-elite-membership-status'>
        <div className='overflow-hidden rounded-2xl bg-[color:var(--ep-card)] shadow-[var(--ep-shadow)] animate-pulse'>
          <div className='flex items-start justify-between gap-3 px-5 py-4'>
            <div className='space-y-2'>
              <div className='h-[14px] w-24 rounded bg-[color:var(--ep-ink-muted)] opacity-25' />
              <div className='h-[13px] w-36 rounded bg-[color:var(--ep-ink-muted)] opacity-15' />
            </div>
            <div className='space-y-2 text-right'>
              <div className='ml-auto h-[14px] w-20 rounded bg-[color:var(--ep-ink-muted)] opacity-25' />
              <div className='ml-auto h-[13px] w-16 rounded bg-[color:var(--ep-ink-muted)] opacity-15' />
            </div>
          </div>
          <hr className='border-[color:var(--ep-border)]' />
          <div className='px-5 py-4'>
            <div className='h-[13px] w-40 rounded bg-[color:var(--ep-ink-muted)] opacity-20' />
          </div>
        </div>
      </div>
    )
  }

  if (showRenewalPrompt) {
    return (
      <div className='w-full pb-6' data-testid='elite-plans-elite-membership-status'>
        <div className='flex flex-col gap-4 px-5'>
          <EliteMembershipRenewalStatusCard eliteOnboardingStatus={eliteOnboardingStatus} />
        </div>
        {familyConfig?.isEliteBlockedBecausePrimaryNotElite ? (
          <NewEliteFamilyPrimaryNonEliteOfferCard
            familyMemberDisplayName={familyConfig.secondaryUserName}
            eligiblePlans={eliteOnboardingStatus?.eligiblePlans ?? []}
            eliteIntent={EliteFamilyOfferCardIntent.Renew}
          />
        ) : (
          <EliteMembershipRenewalSection
            eliteOnboardingStatus={eliteOnboardingStatus}
            familyConfig={familyConfig}
          />
        )}
      </div>
    )
  }

  if (hasUpgradePlan) {
    return (
      <div
        className='my-6 flex w-full flex-col gap-4 px-5'
        data-testid='elite-plans-elite-membership-status'
      >
        <EliteMembershipCurrentPlanCard eliteOnboardingStatus={eliteOnboardingStatus} />
        <EliteMembershipUpgradeSection
          eliteOnboardingStatus={eliteOnboardingStatus}
          familyConfig={familyConfig}
        />
      </div>
    )
  }

  if (isExpiredWithoutAutoPay) {
    return (
      <div className='my-6 w-full px-5' data-testid='elite-plans-elite-membership-status'>
        <EliteMembershipRenewalStatusCard eliteOnboardingStatus={eliteOnboardingStatus} />
      </div>
    )
  }

  return (
    <div className='w-full px-5' data-testid='elite-plans-elite-membership-status'>
      <ElitePlansEliteActivePlanCard eliteOnboardingStatus={eliteOnboardingStatus} />
    </div>
  )
}

export function ElitePlansEliteActivePlanCard({
  eliteOnboardingStatus,
  className = 'my-6 w-full'
}: {
  eliteOnboardingStatus?: EliteOnboardingStatusResponse
  className?: string
}) {
  const activePlan = getEliteFocusEligiblePlan(eliteOnboardingStatus)
  const { period: planTenurePeriod, value: planTenureValue } =
    getEliteCurrentPlanTenure(eliteOnboardingStatus)
  const planTenureLabel = getElitePlanTenureLabel({
    period: planTenurePeriod,
    value: planTenureValue
  })
  const planTypeLabel = `Elite ${planTenureLabel}`
  const planDurationSuffix = planTenureLabel === 'Yearly' ? '/yr' : '/mo'
  const planStatusLabel = getElitePlanStatusLabel(eliteOnboardingStatus)
  const priceDisplay = getEliteSubscriptionPlanPriceDisplayExGst(
    eliteOnboardingStatus?.subscriptionDetails,
    activePlan
  )
  const primaryPlanFees = priceDisplay.primaryExGst
  const basePlanFeesAmount = priceDisplay.listStrikeExGst
  const hasEffectivePlanFees = priceDisplay.showStrikeThrough
  const subscriptionExpiresOn = eliteOnboardingStatus?.subscriptionDetails?.subscriptionExpiresOn
  const validityDateLabel = getEliteSubscriptionValidityDateLabel(eliteOnboardingStatus)

  return (
    <div
      className={`overflow-hidden rounded-2xl bg-[color:var(--ep-card)] shadow-[var(--ep-shadow)] ${className}`}
    >
      <div className='flex items-start justify-between gap-3 px-5 py-4'>
        <div>
          <p className='text-sm font-medium text-prime60'>{planTypeLabel}</p>
          <p className='mt-1 text-[13px] font-medium text-[color:var(--ep-accent)] text-left'>
            {planStatusLabel}
          </p>
        </div>
        <div className='text-right'>
          <p className='text-sm font-medium text-[#232323]'>
            {roundOffAndFormatNumberWithCurrency(primaryPlanFees)}
            {planDurationSuffix}
            <span className='text-[12px] font-normal text-[#8E9393]'>+GST</span>
          </p>
          {hasEffectivePlanFees ? (
            <p className='text-[13px] text-[#8E9393] line-through'>
              {roundOffAndFormatNumberWithCurrency(basePlanFeesAmount)}
              {planDurationSuffix}
            </p>
          ) : null}
        </div>
      </div>
      <hr className='border-[color:var(--ep-border)]' />
      <div className='px-5 py-4 text-[13px] text-prime60 text-left'>
        {validityDateLabel}{' '}
        {subscriptionExpiresOn ? convertEpochToDate(subscriptionExpiresOn, '%d %b %Y') : 'N/A'}
      </div>
    </div>
  )
}
